import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Package,
  Sparkles,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Zap,
  Trees,
  PackageCheck,
  ToolCase,
} from "lucide-react";

const services = [
  {
    title: "Agricultural Export",
    description:
      "Exporting premium beans, pulses, oil seeds, and spices to global markets across USA, Europe, Asia, Africa, and North America.",
    icon: Trees,
    color: "from-green-500 to-green-600",
    features: [
      "Beans Export",
      "Pulses Export",
      "Oil Seeds Export",
      "Spices Export",
    ],
    stats: "5+ Continents",
  },
  {
    title: "Quality Processing",
    description:
      "We clean, sort, process, and package our agricultural products to make sure they meet our customer's requirements and standards.",
    icon: PackageCheck,
    color: "from-emerald-500 to-emerald-600",
    features: ["Cleaning", "Sorting", "Processing", "Packaging"],
    stats: "100% Quality",
  },
  {
    title: "Import Services",
    description:
      "Importing essential products from different countries including generators, construction machineries, food products, and other items.",
    icon: ToolCase,
    color: "from-blue-500 to-blue-600",
    features: [
      "Generators",
      "Construction Machinery",
      "Food Products",
      "Other Items",
    ],
    stats: "Diverse Range",
  },
];

const Services = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section
      id="services"
      className="relative py-20 md:py-32 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden scroll-mt-20"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-brand-yellow/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-brand-navy/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-brand-yellow/5 to-brand-navy/5 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10">
        {/* Header Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="text-brand-navy text-3xl font-semibold mb-6 uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Our Services
          </motion.div>

          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Exporting Ethiopia's
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-yellow to-brand-navy">
              Agricultural Excellence
            </span>
          </motion.h2>

          <motion.p
            className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            Since 2015, we've been delivering premium agricultural products to
            global markets, ensuring quality at every step from cleaning to
            packaging.
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const isHovered = hoveredCard === index;

            return (
              <motion.div
                key={index}
                className="group relative"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                onHoverStart={() => setHoveredCard(index)}
                onHoverEnd={() => setHoveredCard(null)}
              >
                <motion.div
                  className={`relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden ${
                    isHovered ? "scale-105" : ""
                  }`}
                  whileHover={{ y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Background Gradient */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                  />

                  {/* Floating Elements */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <motion.div
                      animate={isHovered ? { rotate: 360 } : { rotate: 0 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Zap className="w-6 h-6 text-brand-yellow" />
                    </motion.div>
                  </div>

                  {/* Icon Section */}
                  <div className="relative mb-6">
                    <motion.div
                      className="h-16 w-16 rounded-2xl bg-gray-50 grid place-items-center text-black shadow-sm mb-4 border border-gray-100"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <IconComponent className="w-8 h-8 text-gray-800" />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className="relative">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-navy transition-colors">
                      {service.title}
                    </h3>

                    <p className="text-gray-600 leading-relaxed mb-6">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <motion.div
                          key={idx}
                          className="flex items-center gap-3 text-sm text-gray-600"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{
                            duration: 0.4,
                            delay: index * 0.1 + 0.4 + idx * 0.1,
                          }}
                          viewport={{ once: true }}
                        >
                          <CheckCircle className="w-4 h-4 text-brand-yellow" />
                          <span>{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Hover Border */}
                  <div
                    className={`absolute inset-0 rounded-3xl border-2 border-transparent bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-20 transition-opacity duration-500`}
                    style={{
                      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                    }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
